<script setup lang="ts">
import moment from "moment";
import { isNullish, TuTable, TuTableHeader, TuTableRow } from "@tinyurldev/tu-ui-vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";

import type { Organization } from "@Modules/teams/Data/Models/OrganizationModel";
import type { TeamUser } from "@Modules/teams/Data/Models/TeamUserModel";
import useTranslation from "@Composables/useTranslation";
import OrgUserActionDropdown from "@Modules/teams/components/OrgUserActionDropdown.vue";
import Capitalize from "@Resources/js/filters/Capitalize";

//
// Props
interface Props {
  organization: Organization;
  users: TeamUser[];
  loading?: boolean;
  disabled?: boolean;
  deactivate?: (user: TeamUser) => void;
}

const props = withDefaults(defineProps<Props>(), {
  loading: true,
  disabled: false,
  deactivate: () => {},
});

//
// Setup
const { __ } = useTranslation();
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

//
// Methods
function formatLastActive(date?: string | null): string {
  if (isNullish(date)) {
    return "—";
  }

  return moment.utc(date).local().fromNow();
}
</script>

<template>
  <div class="org-user-active-table _active_table">
    <TuTable>
      <template
        v-if="!isMobileView"
        #header
      >
        <TuTableHeader>
          <th>{{ __('organization_users_settings.table_name') }}</th>
          <th>{{ __('organization_users_settings.table_email') }}</th>
          <th class="org-user-active-table__narrow-cell">
            {{ __('organization_users_settings.table_role') }}
          </th>
          <th class="org-user-active-table__narrow-cell">
            {{ __('organization_users_settings.table_last_active') }}
          </th>
          <th class="org-user-active-table__action-cell">
            {{ __('organization_users_settings.table_action') }}
          </th>
        </TuTableHeader>
      </template>

      <TuTableRow
        v-if="loading"
        class="_loading"
      >
        <td colspan="5">
          <i class="fa fa-spinner fa-spin" />
          {{ __('organization_users_settings.table_loading') }}
        </td>
      </TuTableRow>

      <TuTableRow v-else-if="!users.length">
        <td
          class="_no_users text-center"
          colspan="5"
        >
          <i class="fa-solid fa-circle-exclamation" />
          {{ __('organization_users_settings.table_no_users') }}
        </td>
      </TuTableRow>

      <template v-else-if="!isMobileView">
        <TuTableRow
          v-for="user in users"
          :key="user.email"
          class="_user_row"
        >
          <td class="org-user-active-table__clamp-cell _user_name">
            {{ user.name }}
          </td>
          <td class="org-user-active-table__clamp-cell _user_email">
            {{ user.email }}
          </td>
          <td class="org-user-active-table__narrow-cell _user_role">
            {{ Capitalize(user.role ) }}
          </td>
          <td class="org-user-active-table__narrow-cell _user_last_active">
            {{ formatLastActive(user.updated_at) }}
          </td>
          <td class="org-user-active-table__action-cell">
            <OrgUserActionDropdown
              v-if="!props.disabled && !user.isOwner()"
              class="_user_actions"
              @deactivate="deactivate(user)"
            />
            <span
              v-else
              class="_no_action"
            >—</span>
          </td>
        </TuTableRow>
      </template>

      <template v-else>
        <TuTableRow
          v-for="user in users"
          :key="user.email"
          class="_user_row"
        >
          <td colspan="5">
            <div class="org-user-active-table__cell-mobile org-user-active-table__cell-mobile--full">
              <p class="org-user-active-table__cell-mobile-content _user_name">
                {{ user.name }}
              </p>
            </div>
            <div class="org-user-active-table__cell-mobile org-user-active-table__cell-mobile--full">
              <p class="org-user-active-table__cell-mobile-content _user_email">
                {{ user.email }}
              </p>
            </div>
            <div class="org-user-active-table__cell-mobile">
              <p class="org-user-active-table__cell-mobile-title">
                {{ __('organization_users_settings.table_role') }}:
              </p>
              <p class="org-user-active-table__cell-mobile-content _user_role">
                {{ Capitalize(user.role) }}
              </p>
            </div>
            <div class="org-user-active-table__cell-mobile">
              <p class="org-user-active-table__cell-mobile-title">
                {{ __('organization_users_settings.table_last_active') }}:
              </p>
              <p class="org-user-active-table__cell-mobile-content _user_last_active">
                {{ formatLastActive(user.last_active) }}
              </p>
            </div>
            <div class="org-user-active-table__cell-mobile">
              <p class="org-user-active-table__cell-mobile-title">
                {{ __('organization_users_settings.table_action') }}:
              </p>
              <div class="org-user-active-table__cell-mobile-content">
                <OrgUserActionDropdown
                  v-if="!props.disabled && !user.isOwner()"
                  class="_user_actions"
                  @deactivate="deactivate(user)"
                />
                <span
                  v-else
                  class="_no_action"
                >—</span>
              </div>
            </div>
          </td>
        </TuTableRow>
      </template>
    </TuTable>

    <p
      v-if="!loading && users.length"
      class="org-user-active-table__count _active_count"
    >
      {{
        users.length === 1
          ? __('organization_users_settings.active_count_single', { count: users.length })
          : __('organization_users_settings.active_count_many', { count: users.length })
      }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.org-user-active-table {
  &__action-cell {
    width: 10%;
    white-space: nowrap;
  }

  &__narrow-cell {
    width: 15%;
    white-space: nowrap;
  }

  &__count {
    margin: spacing.$xxs 0 0;
    font-weight: typography.$font-weight-bold;
  }

  &__clamp-cell {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__cell-mobile {
    display: grid;
    grid-template-columns: 1fr 1.5fr;
    gap: spacing.$xxxs;

    &:not(:last-child) {
      margin-bottom: spacing.$xxxs;
    }

    &--full {
      grid-template-columns: 1fr;
    }
  }

  &__cell-mobile-title {
    font-weight: typography.$font-weight-bold;
  }

  &__cell-mobile-title,
  &__cell-mobile-content {
    margin: 0;
  }
}
</style>
